import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  selectAbandonedTourStep,
  selectCompletedTours,
  setTourStarted,
} from "../../app/slices/OnboardingSlice";
import { resolveOnboardingTarget } from "./onboardingTargets";
import { useOnboardingStrategy } from "./OnboardingHost";

export interface TourLauncher {
  /** "Take the tour", or "Resume the tour" if one was abandoned part-way. */
  label: string;
  /** False when no tour can run from here: render the control disabled. */
  canStart: boolean;
  startTour: () => void;
  /**
   * The same tour from step one, ignoring any stored resume point. The
   * settings dialog offers this rather than startTour, because "restart the
   * tour for this screen" is a different promise from "carry on where you
   * left off".
   */
  restartTour: () => void;
}

/**
 * Everything needed to offer "take the tour" on a surface, without the surface
 * having to know anything about tours.
 *
 * This lives apart from PiHelpButton because the control is not always shaped
 * the same: the dashboard header and the 3D actionbar want an MUI IconButton,
 * while the 360 editor wants an EditorRailButton so it sits with the rest of
 * the rail. Only the presentation differs, so only the presentation is
 * duplicated.
 */
export function useTourLauncher(): TourLauncher {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const strategy = useOnboardingStrategy();
  const completedTours = useSelector(selectCompletedTours);

  /**
   * Only offer a tour that can actually run in the current project state.
   * Without the canRun check, a populated project would start stage A, whose
   * copy says the project is empty while takes are plainly visible.
   */
  const runnableSignature = useSelector((state: any) =>
    strategy.tours.map((tour) => (tour.canRun(state) ? "1" : "0")).join("")
  );

  const runnable = strategy.tours.filter(
    (_, index) => runnableSignature[index] === "1"
  );

  const replayTour =
    runnable.find((tour) => !completedTours.includes(tour.id)) ??
    runnable[0] ??
    null;

  const resumeStep = useSelector(
    replayTour ? selectAbandonedTourStep(replayTour.id) : () => null
  );

  const label = String(
    resumeStep !== null
      ? t("onboarding.help.resume_tour")
      : t("onboarding.help.take_tour")
  );

  const run = (stepIndex: number) => {
    if (!replayTour) return;

    /**
     * canRun gates on store state, which cannot tell one route from another.
     * Asking the DOM is the only honest test of "can this tour run from where
     * I am standing", and starting one whose anchors are absent would abort it
     * and burn it for the rest of the session.
     */
    const resolves = replayTour.steps.every(
      (step) => resolveOnboardingTarget(step.target) !== null
    );
    if (!resolves) return;

    dispatch(setTourStarted({ tourId: replayTour.id, stepIndex }));
  };

  const startTour = () => run(resumeStep ?? 0);
  const restartTour = () => run(0);

  return { label, canStart: Boolean(replayTour), startTour, restartTour };
}
